import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import sqlInjectionImage from "../assets/blog_images/sqlInjection/sql_injection1.jpg";
import brokenAccessImage from "../assets/blog_images/BrokenAccessControllAuthentication/brokenThumbnail.png";
import sessionImage from "../assets/blog_images/sessionManagement/session1.png";
import sqlmapImage from "../assets/blog_images/sqliWithSqlMap/exploiting-sqli-with-sqlmap.png";
import threatModelingImage from "../assets/blog_images/threatModeling/cover.jpeg";
import reconImage from "../assets/blog_images/ReconnaissanceTechniquesInPenetratingTesting/thumbnail.png";
import pentestingImage from "../assets/blog_images/PenetratingTesting/Title.png";

const SUPPORT_WHATSAPP_NUMBER = "919080432374";
const SUPPORT_MESSAGE = "Hi, I am looking for Cyber Security Training";

const blogArticles = [
  {
    id: 1,
    title: "Key Types Of Penetration Testing",
    category: "PenetrationTesting",
    categoryLabel: "Penetration Testing",
    date: "June 20, 2025",
    readTime: "7 min read",
    image: pentestingImage,
    url: "/penetration-testing",
    featured: true,
    description:
      "Explore the core types of penetration testing used by ethical hackers to uncover vulnerabilities — from network to application and beyond.",
    tags: ["VAPT", "Security Testing", "Ethical Hacking"],
  },
  {
    id: 2,
    title: "Reconnaissance Techniques in Penetration Testing: A Hands-On Approach",
    category: "WebApplicationSecurity",
    categoryLabel: "Web Application Security",
    date: "June 18, 2025",
    readTime: "10 min read",
    image: reconImage,
    url: "/reconnaissance-in-penetration-testing",
    featured: true,
    description:
      "Master the first step of ethical hacking with practical reconnaissance techniques used during penetration testing engagements.",
    tags: ["Recon", "Pentesting", "OSINT"],
  },
  {
    id: 3,
    title: "Beginner's Practical Guide to Threat Modeling",
    category: "ThreatModeling",
    categoryLabel: "Threat Modeling",
    date: "May 22, 2025",
    readTime: "10 min read",
    image: threatModelingImage,
    url: "/threat-modeling",
    featured: false,
    description:
      "Threat modeling is a proactive cybersecurity process used to identify and mitigate potential threats before they can be exploited.",
    tags: ["Design Security", "Risk", "Architecture"],
  },
  {
    id: 4,
    title: "Exploiting SQL Injections Automatically Using SQLmap",
    category: "WebApplicationSecurity",
    categoryLabel: "Web Application Security",
    date: "May 06, 2025",
    readTime: "5 min read",
    image: sqlmapImage,
    url: "/sqli-with-sqlmap",
    featured: false,
    description:
      "SQLmap is an open-source tool designed to automate the detection and exploitation of SQL injection flaws.",
    tags: ["SQLmap", "SQL Injection", "Automation"],
  },
  {
    id: 5,
    title: "Understanding SQL Injection: A Web Security Threat",
    category: "WebApplicationSecurity",
    categoryLabel: "Web Application Security",
    date: "May 03, 2025",
    readTime: "7 min read",
    image: sqlInjectionImage,
    url: "/sql-injection",
    featured: false,
    description:
      "SQL Injection is a critical security flaw that allows attackers to manipulate SQL queries, risking data breaches or full database control.",
    tags: ["OWASP", "Database", "Injection"],
  },
  {
    id: 6,
    title: "Broken Access Control & Auth: A Security Breakdown",
    category: "OWASPTop10",
    categoryLabel: "OWASP Top 10",
    date: "May 02, 2025",
    readTime: "5 min read",
    image: brokenAccessImage,
    url: "/broken-access-control-authentication",
    featured: false,
    description:
      "Broken Access Control and Authentication vulnerabilities allow unauthorized users to access restricted resources.",
    tags: ["OWASP", "Access Control", "Authentication"],
  },
  {
    id: 7,
    title: "Secure Session Management: Best Practices & Techniques",
    category: "WebApplicationSecurity",
    categoryLabel: "Web Application Security",
    date: "Apr 28, 2025",
    readTime: "5 min read",
    image: sessionImage,
    url: "/session-management",
    featured: false,
    description:
      "Learn techniques to prevent session-related threats such as session hijacking, session fixation, and weak cookie handling.",
    tags: ["Session", "Cookies", "Web Security"],
  },
];

const categories = [
  { label: "All Categories", value: "all" },
  { label: "Web Application Security", value: "WebApplicationSecurity" },
  { label: "OWASP Top 10", value: "OWASPTop10" },
  { label: "Threat Modeling", value: "ThreatModeling" },
  { label: "Penetration Testing", value: "PenetrationTesting" },
];

function Blog() {
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    document.title = "Blogs | Practical Infosec";

    function handleScroll() {
      setShowBackToTop(window.scrollY > 350);
    }

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const filteredArticles = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return blogArticles.filter((article) => {
      const matchesSearch =
        !search ||
        article.title.toLowerCase().includes(search) ||
        article.description.toLowerCase().includes(search) ||
        article.categoryLabel.toLowerCase().includes(search) ||
        article.tags.some((tag) => tag.toLowerCase().includes(search));

      const matchesCategory =
        selectedCategory === "all" || article.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchText, selectedCategory]);

  const featuredArticle = blogArticles.find((article) => article.featured);
  const totalReadMinutes = blogArticles.reduce((total, article) => {
    const minutes = Number.parseInt(article.readTime, 10);
    return Number.isNaN(minutes) ? total : total + minutes;
  }, 0);

  function toggleWhatsAppModal(event) {
  if (event) {
    event.stopPropagation();
  }

  setWhatsappOpen((prev) => !prev);
}
  function openWhatsAppChat() {
    const encodedMessage = encodeURIComponent(SUPPORT_MESSAGE);
    const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  }

  function openWhatsAppAppFallback() {
    const encodedMessage = encodeURIComponent(SUPPORT_MESSAGE);
    const deepLink = `whatsapp://send?phone=${SUPPORT_WHATSAPP_NUMBER}&text=${encodedMessage}`;
    const webLink = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodedMessage}`;

    window.location.href = deepLink;

    setTimeout(() => {
      window.open(webLink, "_blank", "noopener,noreferrer");
    }, 700);
  }

  return (
    <main className="overflow-hidden bg-white">
      {/* HERO */}
      <section className="relative px-4 py-20 overflow-hidden bg-slate-950 md:px-20 md:py-24">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.35),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.25),transparent_35%),radial-gradient(circle_at_center,rgba(124,58,237,0.18),transparent_42%)]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/70 to-transparent"></div>
        </div>

        <div className="relative z-10 grid items-center gap-12 mx-auto max-w-7xl lg:grid-cols-[1fr_0.9fr]">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15 backdrop-blur">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              Practical Infosec Knowledge Hub
            </span>

            <h1 className="mt-6 text-4xl font-black leading-tight tracking-tight text-white md:text-6xl">
              Practical Cybersecurity{" "}
              <span className="text-transparent bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text">
                Articles & Research
              </span>
            </h1>

            <p className="max-w-3xl mt-6 text-lg font-semibold leading-8 text-slate-300">
              Deep technical blogs on web application security, OWASP Top 10,
              threat modeling, penetration testing, reconnaissance, SQL
              injection, session security, and practical VAPT workflows.
            </p>

            <div className="grid max-w-4xl grid-cols-2 gap-4 mt-10 sm:grid-cols-4">
              <div className="p-4 border rounded-2xl bg-white/10 border-white/15 backdrop-blur">
                <p className="text-2xl font-black text-white">
                  {blogArticles.length}+
                </p>
                <p className="mt-1 text-xs font-bold text-slate-300">
                  Articles
                </p>
              </div>

              <div className="p-4 border rounded-2xl bg-white/10 border-white/15 backdrop-blur">
                <p className="text-2xl font-black text-white">
                  {categories.length - 1}
                </p>
                <p className="mt-1 text-xs font-bold text-slate-300">
                  Categories
                </p>
              </div>

              <div className="p-4 border rounded-2xl bg-white/10 border-white/15 backdrop-blur">
                <p className="text-2xl font-black text-white">
                  {totalReadMinutes}+
                </p>
                <p className="mt-1 text-xs font-bold text-slate-300">
                  Min Reading
                </p>
              </div>

              <div className="p-4 border rounded-2xl bg-white/10 border-white/15 backdrop-blur">
                <p className="text-2xl font-black text-white">VAPT</p>
                <p className="mt-1 text-xs font-bold text-slate-300">
                  Focused
                </p>
              </div>
            </div>
          </div>

          {featuredArticle && (
            <a
              href={featuredArticle.url}
              target="_blank"
              rel="noopener noreferrer"
              className="relative block transition group hover:-translate-y-2"
            >
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-r from-blue-500/40 via-cyan-400/30 to-purple-500/30 blur-2xl"></div>

              <div className="relative overflow-hidden border shadow-2xl rounded-[2rem] border-white/15 bg-white/10 backdrop-blur">
                <img
                  src={featuredArticle.image}
                  alt={featuredArticle.title}
                  className="object-cover w-full h-72 sm:h-96"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent"></div>

                <div className="absolute inset-x-5 bottom-5">
                  <span className="inline-flex px-3 py-1 text-xs font-black border rounded-full text-cyan-300 bg-white/10 border-white/15">
                    Featured Article
                  </span>

                  <h2 className="mt-3 text-2xl font-black leading-tight text-white">
                    {featuredArticle.title}
                  </h2>

                  <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">
                    {featuredArticle.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 mt-4 border-t border-white/10">
                    <span className="text-xs font-bold text-slate-300">
                      {featuredArticle.date}
                    </span>
                    <span className="text-xs font-bold text-slate-300">
                      {featuredArticle.readTime}
                    </span>
                  </div>
                </div>
              </div>
            </a>
          )}
        </div>
      </section>

      {/* SEARCH + FILTER */}
      <section className="relative px-4 py-10 overflow-hidden border-b bg-gradient-to-br from-white via-slate-50 to-blue-50 md:px-20 border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_260px]">
            <div className="relative">
              <span className="absolute text-lg -translate-y-1/2 left-4 top-1/2 text-slate-400">
                🔎
              </span>

              <input
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search by blog title, category, tag, or topic..."
                className="w-full py-4 pl-12 pr-4 text-base font-bold bg-white border shadow-sm outline-none rounded-2xl border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value)}
              className="w-full px-4 py-4 text-base font-black bg-white border shadow-sm outline-none rounded-2xl border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-5">
            {categories.map((category) => {
              const active = selectedCategory === category.value;

              return (
                <button
                  key={category.value}
                  type="button"
                  onClick={() => setSelectedCategory(category.value)}
                  className={`px-4 py-2 text-xs font-black transition border rounded-full ${
                    active
                      ? "text-white bg-blue-700 border-blue-700 shadow-lg"
                      : "text-slate-700 bg-white border-slate-200 hover:bg-slate-950 hover:text-white"
                  }`}
                >
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="relative px-4 py-20 overflow-hidden bg-slate-50 md:px-20">
        <div className="absolute top-0 right-0 rounded-full w-96 h-96 bg-blue-200/30 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 rounded-full w-96 h-96 bg-cyan-200/20 blur-3xl"></div>

        <div className="relative z-10 mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 mb-10 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full shadow-sm">
                Blog Library
              </span>

              <h2 className="mt-4 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
                Latest Security{" "}
                <span className="text-blue-700">Articles</span>
              </h2>

              <p className="max-w-3xl mt-4 text-lg font-semibold leading-8 text-slate-600">
                Explore practical security content designed for learners,
                developers, security engineers, and penetration testers.
              </p>
            </div>

            <div className="p-4 bg-white border shadow-lg rounded-2xl border-slate-200">
              <p className="text-xs font-black tracking-widest uppercase text-slate-400">
                Showing
              </p>
              <p className="text-2xl font-black text-slate-950">
                {filteredArticles.length} Article
                {filteredArticles.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          {filteredArticles.length === 0 ? (
            <div className="p-10 text-center bg-white border shadow-xl rounded-3xl border-slate-200">
              <p className="text-2xl font-black text-slate-950">
                No articles found
              </p>
              <p className="mt-2 text-base font-semibold text-slate-600">
                Try another keyword or select a different category.
              </p>

              <button
                type="button"
                onClick={() => {
                  setSearchText("");
                  setSelectedCategory("all");
                }}
                className="px-6 py-3 mt-5 text-sm font-black text-white transition bg-blue-700 rounded-xl hover:bg-blue-800"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {filteredArticles.map((article) => (
                <article
                  key={article.id}
                  className="overflow-hidden transition bg-white border shadow-lg group rounded-3xl border-slate-200 hover:-translate-y-2 hover:border-blue-300 hover:shadow-2xl"
                >
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="object-cover w-full h-56 transition duration-500 group-hover:scale-110"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent"></div>

                      <span className="absolute px-3 py-1 text-xs font-black text-white bg-blue-700 rounded-full left-4 bottom-4">
                        {article.categoryLabel}
                      </span>
                    </div>
                  </a>

                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 text-[11px] font-black text-slate-700 border rounded-full bg-slate-50 border-slate-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <h3 className="text-xl font-black leading-snug transition text-slate-950 group-hover:text-blue-700">
                        {article.title}
                      </h3>
                    </a>

                    <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
                      {article.description}
                    </p>

                    <div className="flex items-center justify-between pt-5 mt-5 text-sm font-bold border-t text-slate-500 border-slate-200">
                      <span>{article.date}</span>
                      <span>{article.readTime}</span>
                    </div>

                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full px-5 py-3 mt-5 text-sm font-black text-white transition bg-slate-950 rounded-xl hover:bg-blue-700"
                    >
                      Read Article →
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-16 bg-white md:px-20">
        <div className="relative max-w-6xl p-8 mx-auto overflow-hidden shadow-2xl rounded-[2rem] bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 md:p-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.25),transparent_35%)]"></div>

          <div className="relative z-10 grid items-center grid-cols-1 gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                Want to learn practically?
              </p>

              <h2 className="mt-3 text-3xl font-black text-white md:text-4xl">
                Turn blog knowledge into real cybersecurity skills.
              </h2>

              <p className="mt-4 text-base font-semibold leading-7 text-slate-300">
                Join our live Junior or Senior Penetration Tester programs and
                learn with guided labs, mentor support, and job-focused training.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Link
                to="/junior-penetration-tester#demoRegistration"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition px-7 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 hover:scale-105"
              >
                Apply for Junior
              </Link>

              <Link
                to="/senior-penetration-tester#demoRegistration"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition px-7 rounded-2xl bg-gradient-to-r from-red-600 via-purple-700 to-blue-700 hover:scale-105"
              >
                Apply for Senior
              </Link>
            </div>
          </div>
        </div>
      </section>

       {/* WHATSAPP STICKY BUTTON */}
<button
  type="button"
  onClick={toggleWhatsAppModal}
  className="fixed z-50 p-3 px-4 text-white transition -translate-y-1/2 rounded-full shadow-lg cursor-pointer top-1/2 right-6 bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:opacity-90 animate-pulse"
  aria-label="Join WhatsApp"
>
  <i className="text-4xl fa-brands fa-whatsapp"></i>
</button>

{/* WHATSAPP MODAL */}
{whatsappOpen && (
  <div
    className="fixed z-50 p-4 transition-opacity duration-300 -translate-y-1/2 bg-white shadow-xl top-1/2 right-20 rounded-xl w-72"
    role="dialog"
    aria-modal="true"
    aria-labelledby="whatsappTitle"
    onClick={(event) => event.stopPropagation()}
  >
    {/* Close Button */}
    <button
      type="button"
      onClick={toggleWhatsAppModal}
      className="absolute text-3xl text-gray-600 cursor-pointer top-2 left-4 hover:text-red-500"
      aria-label="Close"
    >
      &times;
    </button>

    <h2
      id="whatsappTitle"
      className="mt-6 mb-2 text-lg font-bold text-center text-gray-800"
    >
      Unsure about which course to choose?
    </h2>

    <p className="mb-3 text-sm text-center text-gray-700">
      Tap below to start a WhatsApp chat — we're here to help!
    </p>

    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={openWhatsAppChat}
        className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-white transition bg-green-600 rounded-full hover:bg-green-700"
        aria-label="Chat Now on WhatsApp"
      >
        <i className="fa-brands fa-whatsapp"></i>
        Chat Now
      </button>

      <button
        type="button"
        onClick={openWhatsAppAppFallback}
        className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-gray-800 transition border border-gray-200 rounded-full hover:bg-gray-50"
        aria-label="Open in WhatsApp App"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-4 h-4 text-green-600"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M20.52 3.48A11.93 11.93 0 0012 .5C5.73.5.99 5.24.99 11.5c0 2.01.53 3.95 1.53 5.68L0 24l6.97-1.79c1.61.45 3.28.68 4.9.68 6.27 0 11.01-4.74 11.01-11 0-3.02-1.18-5.85-3.36-7.41zM12 21.5c-1.44 0-2.86-.34-4.14-.99l-.3-.15-4.14 1.06 1.15-3.98-.18-.31A8.12 8.12 0 013 11.5c0-4.97 4.06-9 9-9 2.41 0 4.66.94 6.36 2.64a8.95 8.95 0 012.64 6.36c0 4.97-4.06 9-9 9z" />
        </svg>
        Open in App
      </button>
    </div>
  </div>
)}

{/* FLOATING CALL BUTTON */}
<a
  href="tel:+91-9080432374"
  className="fixed z-50 p-3 text-white transition rounded-md shadow-lg cursor-pointer bg-violet-600 bottom-60 right-7 hover:bg-green-700"
  aria-label="Call Us"
>
  <i className="text-2xl fa-solid fa-phone"></i>
</a>

{/* BACK TO TOP BUTTON */}
{showBackToTop && (
  <button
    type="button"
    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    className="fixed z-50 p-4 text-white transition bg-blue-700 rounded-lg shadow-lg cursor-pointer bottom-12 right-7 hover:bg-blue-800"
    aria-label="Back to top"
  >
    <i className="text-xl fa-solid fa-arrow-up"></i>
  </button>
)}
    </main>
  );
}

export default Blog;